import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { cn } from "~/common/shadcn-utils"
import { gql } from "~/__generated__"
import XIcon from "~/images/icons/x.svg?react"
import Toggle from "react-toggle"
import {
  CandidateTestStateEnum,
  CandidateTestsOrganizationFilter,
} from "~/__generated__/graphql"
import {
  CandidateTestsFilterDropdown,
  FilterOption,
} from "./CandidateTestsFilterDropdown"
import { Search } from "lucide-react"
import { useDebouncedCallback } from "use-debounce"

export const CandidateTestsTableTopFilter = ({
  organizationId,
  filter,
  onFilterChange,
  allowTestFilter = true,
  searchParamsPrefix = "responses_",
}: {
  organizationId: string
  filter: CandidateTestsOrganizationFilter
  onFilterChange: (filter: CandidateTestsOrganizationFilter) => void
  allowTestFilter?: boolean
  searchParamsPrefix?: string
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState("")

  const { loading, data, error } = useQuery(ORGANIZATION_TESTS_QUERY_DOCUMENT, {
    variables: { organizationId },
  })

  const {
    loading: reviewStatusesLoading,
    data: reviewStatusesData,
    error: reviewStatusError,
  } = useQuery(REVIEW_STATUSES_QUERY_DOCUMENT, {
    variables: { organizationId },
  })

  if (error || reviewStatusError) {
    console.error("Error loading tests for filter", error)
  }

  const handleFilterChange = (newFilter: CandidateTestsOrganizationFilter) => {
    searchParams.delete(`${searchParamsPrefix}page`)
    setSearchParams(searchParams)
    onFilterChange(newFilter)
  }

  const clearFilters = () => {
    handleFilterChange({
      ...filter,
      testId: null,
      candidateTestStates: [],
      candidateTestReviewStatus: null,
    })
  }

  const stateFilterOptionMap: Record<CandidateTestStateEnum, string> = {
    [CandidateTestStateEnum.NotStarted]: "Not Started",
    [CandidateTestStateEnum.InProgress]: "In Progress",
    [CandidateTestStateEnum.Completed]: "Completed",
  }

  const reviewStatusFilterOptionMap: Record<string, string> =
    reviewStatusesData?.reviewStatuses?.nodes.reduce(
      (acc, status) => {
        acc[status.id] = status.name
        return acc
      },
      {} as Record<string, string>
    ) || {}

  const stateFilterOptions = Object.entries(stateFilterOptionMap).map(
    ([key, value]) => ({ id: key as CandidateTestStateEnum, name: value })
  )

  const onStateFilterChanged = (selectedOptions: FilterOption[]) => {
    const newStates = selectedOptions.map(
      (option) => option.id as CandidateTestStateEnum
    )
    handleFilterChange({ ...filter, candidateTestStates: newStates })
  }

  const onTestIdChanged = (filterOption: FilterOption | undefined) => {
    handleFilterChange({ ...filter, testId: filterOption?.id })
  }

  const reviewStatusFilterOptions = Object.entries(
    reviewStatusFilterOptionMap
  ).map(([key, value]) => ({
    id: key as string,
    name: value,
  }))

  const onReviewStatusChanged = (selectedOption: FilterOption | undefined) => {
    handleFilterChange({
      ...filter,
      candidateTestReviewStatus: selectedOption?.id as string,
    })
  }

  const onQueryChanged = useDebouncedCallback((query: string) => {
    handleFilterChange({ ...filter, query })
  }, 250)

  const tests = data?.tests?.nodes
  const testFilterOptions: FilterOption[] =
    tests?.map((test) => ({ id: test.id, name: test.name })) || []

  const selectedTestFilterOption = filter.testId
    ? testFilterOptions.find((option) => option.id === filter.testId)
    : undefined

  const selectedStateFilterOptions =
    filter.candidateTestStates && filter.candidateTestStates.length > 0
      ? filter.candidateTestStates.map(
          (id) => ({ id: id, name: id }) as FilterOption
        )
      : []

  const selectedReviewStatusFilterOption = filter.candidateTestReviewStatus
    ? reviewStatusFilterOptions.find(
        (option) => option.id === filter.candidateTestReviewStatus
      )
    : undefined

  return (
    <div className="flex flex-row items-center justify-between">
      <div className="flex items-center pr-6">
        <label className="block relative">
          <Search
            color="#999999"
            size={16}
            className="absolute top-[10px] left-[10px]"
          />
          <input
            className={cn(
              "w-full border-1 border-gray-E3E2E0 rounded w-[250px] pl-10"
            )}
            placeholder="Search by name or email"
            value={query || ""}
            onChange={(e) => {
              setQuery(e.target.value)
              onQueryChanged(e.target.value)
            }}
          />
        </label>
      </div>
      <div className="flex flex-row items-center gap-1">
        {(filter.testId ||
          (filter.candidateTestStates &&
            filter.candidateTestStates.length > 0) ||
          (filter.candidateTestReviewStatus &&
            filter.candidateTestReviewStatus.length > 0)) && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex flex-row gap-1 items-center text-[#F14C76] p-4 text-sm"
          >
            <XIcon className="h-4 w-4" />
            <span>Clear Filters</span>
          </button>
        )}

        {allowTestFilter && (
          <CandidateTestsFilterDropdown
            filterOptions={testFilterOptions}
            selectedFilterOptions={
              selectedTestFilterOption ? [selectedTestFilterOption] : []
            }
            onFilterChange={(option) => onTestIdChanged(option[0])}
            loading={loading}
            buttonLabel="Test Name"
            popoverHeader="Filter by test name"
            negateFilterLabel="All Tests"
          />
        )}

        <CandidateTestsFilterDropdown
          filterOptions={reviewStatusFilterOptions}
          selectedFilterOptions={
            selectedReviewStatusFilterOption
              ? [selectedReviewStatusFilterOption]
              : []
          }
          onFilterChange={(option) => onReviewStatusChanged(option[0])}
          loading={reviewStatusesLoading}
          buttonLabel="Review Status"
          popoverHeader="Filter by review status"
          negateFilterLabel="All statuses"
        />

        <CandidateTestsFilterDropdown
          filterOptions={stateFilterOptions}
          selectedFilterOptions={selectedStateFilterOptions}
          onFilterChange={onStateFilterChanged}
          loading={loading}
          buttonLabel="Test Status"
          popoverHeader="Filter by test status"
          negateFilterLabel="All statuses"
          allowMultiple={true}
        />
      </div>
    </div>
  )
}

export const CandidateTestsTableBottomFilter = ({
  filter,
  onFilterChange,
  searchParamsPrefix = "responses_",
}: {
  filter: CandidateTestsOrganizationFilter
  onFilterChange: (filter: CandidateTestsOrganizationFilter) => void
  searchParamsPrefix?: string
}) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const handleFilterChange = (newFilter: CandidateTestsOrganizationFilter) => {
    searchParams.delete(`${searchParamsPrefix}page`)
    setSearchParams(searchParams)
    onFilterChange(newFilter)
  }

  return (
    <div className="flex flex-row items-center justify-end">
      <label className="flex items-center gap-2">
        <Toggle
          icons={false}
          defaultChecked={filter.archived || false}
          onChange={(e) =>
            handleFilterChange({ ...filter, archived: e.target.checked })
          }
        />
        <span className="text-gray-333">Include Archived</span>
      </label>
    </div>
  )
}

gql(`
  fragment Org_OrgCandidatesTable on CandidateTest {
    id
    state
    timeTakenSeconds
    test {
      id
      name
    }
    user {
      name
      email
    }
    completedAt
    manualScore
  }
`)

const ORGANIZATION_TESTS_QUERY_DOCUMENT = gql(`
  query OrganizationTests($organizationId: ID!) {
    tests(organizationId: $organizationId) {
      nodes {
        id
        name
      }
    }
  }
`)

const REVIEW_STATUSES_QUERY_DOCUMENT = gql(/* GraphQL */ `
  query reviewStatuses($organizationId: ID!) {
    reviewStatuses(organizationId: $organizationId) {
      nodes {
        id
        name
        position
      }
    }
  }
`)
